import { createSlice } from "@reduxjs/toolkit";
import { StoreDispatch } from "../store";
import { refreshGroupStatsAction } from "../group/groupSlice";
import {
  connectUserToGroup,
  getGroupCard,
  getGroupUser,
  getGroupWallets,
  removeCardFromGroup,
  getGroupDetail,
  removeUserFromGroup,
  connectCardToGroup,
  saveGroupPreferences,
  SaveGroupPreferencesRequest
} from "src/network/graphql/groupService";
import { Card } from "../cards/cardSlice";
import { immediateToast } from "izitoast-react";
import { searchExpenses } from "src/network/graphql/expenseService";
import { Expense } from "../expense/expenseSlice";
import { GroupType, LicenceTagType } from "src/types/commonTypes";

export type GroupUser = {
  display_id: string;
  email: string;
  is_active: boolean;
  user_id: string;
  current_organisation_role: string;
  created_at: string;
  roles: {
    organisation_id: string;
    value: string;
  };
  attributes: {
    first_name: string;
    last_name: string;
  };
};

export type GroupWallet = {
  description: string;
  display_id: string;
  name: string;
  wallet_id: string;
  wallet_type: string;
  wallet_status: string;
  balance: number;
  owner_name: string;
  owner_id: string;
};

export type GroupDetail = {
  group_id: string;
  name: string;
  description: string;
  group_type: GroupType;
  created_by: string;
  group_status: string;
  display_id: string;
  region_id: string;
  external_id: string;
  owner: {
    user_id: string;
    email: string;
    phone: string;
    attributes: {
      first_name: string;
      last_name: string;
      address: {
        line_1: string;
        line_2: string;
        city: string;
        state: string;
        post_code: string;
        country: string;
      };
    };
  };
  wallets: GroupWallet[];
  can_update_group: boolean;
  preferences: {
    exclude_license: boolean;
    licence_tag?: LicenceTagType | null;
  };
};

export const initialGroupUser: GroupUser[] = [];
export const initialGroupCard: Card[] = [];
export const initialGroupDetail: GroupDetail = {} as GroupDetail;

const groupDetailSlice = createSlice({
  name: "groupDetail",
  initialState: {
    loading: false,
    error: null,
    groupDetail: initialGroupDetail,
    groupUserLoading: false,
    groupCardLoading: false,
    groupUser: {
      list: initialGroupUser,
      total: 0
    },
    groupCard: {
      list: initialGroupCard,
      total: 0
    },
    savePreferencesLoading: false,
    expenseLoading: false,
    expenses: {
      list: [] as Expense[],
      total: 0
    },
    userWalletsLoading: false,
    userWallets: [] as GroupWallet[]
  },
  reducers: {
    fetchGroupDetailStart: (state) => {
      state.loading = true;
    },
    fetchGroupDetailSuccess: (state, action) => {
      state.loading = false;
      state.groupDetail = action.payload ?? initialGroupDetail;
    },
    fetchGroupDetailFail: (state, action) => {
      state.loading = false;
      state.groupDetail = initialGroupDetail;
      state.error = action.payload;
    },
    fetchGroupUserStart: (state) => {
      state.groupUserLoading = true;
    },
    fetchGroupUserSuccess: (state, action) => {
      state.groupUserLoading = false;
      state.groupUser.list = action.payload?.list ?? [];
      state.groupUser.total = action.payload?.total ?? 0;
    },
    fetchGroupUserFail: (state, action) => {
      state.groupUserLoading = false;
      state.groupUser.list = [];
      state.groupUser.total = 0;
      state.error = action.payload;
    },
    fetchGroupCardStart: (state) => {
      state.groupCardLoading = true;
    },
    fetchGroupCardSuccess: (state, action) => {
      state.groupCardLoading = false;
      state.groupCard.list = action.payload?.list ?? [];
      state.groupCard.total = action.payload?.total ?? 0;
    },
    fetchGroupCardFail: (state, action) => {
      state.groupCardLoading = false;
      state.groupCard.list = [];
      state.groupCard.total = 0;
      state.error = action.payload;
    },
    connectGroupUserStart: (state) => {
      state.groupUserLoading = true;
    },
    connectGroupUserSuccess: (state) => {
      state.groupUserLoading = false;
    },
    connectGroupUserFail: (state, action) => {
      state.groupUserLoading = false;
      state.error = action?.payload;
    },
    deleteGroupUserStart: (state) => {
      state.groupUserLoading = true;
    },
    deleteGroupUserSuccess: (state) => {
      state.groupUserLoading = false;
    },
    deleteGroupUserFail: (state, action) => {
      state.groupUserLoading = false;
      state.error = action?.payload;
    },
    connectGroupCardStart: (state) => {
      state.groupCardLoading = true;
    },
    connectGroupCardSuccess: (state) => {
      state.groupCardLoading = false;
    },
    connectGroupCardFail: (state, action) => {
      state.groupCardLoading = false;
      state.error = action?.payload;
    },
    deleteGroupCardStart: (state) => {
      state.groupCardLoading = true;
    },
    deleteGroupCardSuccess: (state) => {
      state.groupCardLoading = false;
    },
    deleteGroupCardFail: (state, action) => {
      state.groupCardLoading = false;
      state.error = action?.payload;
    },
    searchExpenseStart: (state) => {
      state.expenseLoading = true;
    },
    searchExpenseSuccess: (state, action) => {
      state.expenseLoading = false;
      state.expenses = action.payload;
    },
    searchExpenseFail: (state, action) => {
      state.expenseLoading = false;
      state.error = action?.payload;
    },
    saveGroupPreferencesStart: (state) => {
      state.savePreferencesLoading = true;
    },
    saveGroupPreferencesSuccess: (state) => {
      state.savePreferencesLoading = false;
    },
    saveGroupPreferencesFail: (state, action) => {
      state.savePreferencesLoading = false;
      state.error = action?.payload;
    },
    fetchUserWalletsStart: (state) => {
      state.userWalletsLoading = true;
    },
    fetchUserWalletsSuccess: (state, action) => {
      state.userWalletsLoading = false;
      state.userWallets = action.payload ?? [];
    },
    fetchUserWalletsFail: (state, action) => {
      state.userWalletsLoading = false;
      state.error = action?.payload;
    }
  }
});

const {
  fetchGroupDetailFail,
  fetchGroupDetailStart,
  fetchGroupDetailSuccess,
  fetchGroupUserFail,
  fetchGroupUserStart,
  fetchGroupUserSuccess,
  fetchGroupCardFail,
  fetchGroupCardStart,
  fetchGroupCardSuccess,
  connectGroupUserStart,
  connectGroupUserSuccess,
  connectGroupUserFail,
  deleteGroupUserStart,
  deleteGroupUserSuccess,
  deleteGroupUserFail,
  connectGroupCardStart,
  connectGroupCardSuccess,
  connectGroupCardFail,
  deleteGroupCardStart,
  deleteGroupCardSuccess,
  deleteGroupCardFail,
  searchExpenseStart,
  searchExpenseSuccess,
  searchExpenseFail,
  saveGroupPreferencesStart,
  saveGroupPreferencesSuccess,
  saveGroupPreferencesFail,
  fetchUserWalletsStart,
  fetchUserWalletsSuccess,
  fetchUserWalletsFail
} = groupDetailSlice.actions;

export const fetchGroupDetailAction = (groupId: string) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchGroupDetailStart());
    try {
      const response = await getGroupDetail(groupId);

      dispatch(fetchGroupDetailSuccess(response.data.get_group));
    } catch (error) {
      dispatch(fetchGroupDetailFail(error));
    }
  };
};

export const getGroupUserAction = (id: string) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchGroupUserStart());
    try {
      const response = await getGroupUser(id);

      dispatch(
        fetchGroupUserSuccess({
          list: response.data.get_group_users,
          total: response.data.get_group_users.length
        })
      );
    } catch (err) {
      dispatch(fetchGroupUserFail(err));
    }
  };
};

export const getGroupCardAction = (id: string) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchGroupCardStart());
    try {
      const response = await getGroupCard(id);

      const structuredResponse = response.data.get_group_cards?.map(
        (card: any) =>
          ({
            id: card.card_id,
            bsb: card.bsb,
            cardStatus: card.card_status,
            accessType: card.card_type,
            cardNumber: card.ppan,
            orderId: card.order_id,
            clientId: card.clientId,
            cardLimit: card.card_limit,
            cardHolderName: card.holder_name,
            created_at: card.created_at,
            user: {
              id: card?.user_id,
              firstName: card?.user?.attributes?.first_name ?? "---",
              lastName: card?.user?.attributes?.last_name ?? " ",
              email: card?.user?.email ?? " "
            }
          }) as Card
      );

      dispatch(
        fetchGroupCardSuccess({
          list: structuredResponse,
          total: response.data.get_group_cards.length
        })
      );
    } catch (err) {
      dispatch(fetchGroupCardFail(err));
    }
  };
};

export const connectGroupUserAction = (groupId: string, userId: string, cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(connectGroupUserStart());
    try {
      await connectUserToGroup(groupId, userId);

      dispatch(connectGroupUserSuccess());
      immediateToast("success", {
        message: "User connected to site/cost centre successfully.",
        timeout: 3000,
        position: "topCenter"
      });
      dispatch(refreshGroupStatsAction([groupId]));
      if (cb) cb();
    } catch (err) {
      dispatch(connectGroupUserFail(err));
    }
  };
};

export const deleteGroupUserAction = (groupId: string, userId: string, cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(deleteGroupUserStart());
    try {
      await removeUserFromGroup(groupId, userId);

      dispatch(deleteGroupUserSuccess());
      immediateToast("success", {
        message: "User removed from Site/Cost Centre Successfully.",
        timeout: 3000,
        position: "topCenter"
      });
      dispatch(refreshGroupStatsAction([groupId]));
      if (cb) cb();
    } catch (err) {
      dispatch(deleteGroupUserFail(err));
    }
  };
};

export const connectGroupCardAction = (groupId: string, cardId: string, cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(connectGroupCardStart());
    try {
      await connectCardToGroup(groupId, cardId);

      dispatch(connectGroupCardSuccess());
      immediateToast("success", {
        message: "Card Connect to Site/Cost Centre Successfully.",
        timeout: 3000,
        position: "topCenter"
      });
      dispatch(refreshGroupStatsAction([groupId]));
      if (cb) cb();
    } catch (err) {
      dispatch(connectGroupCardFail(err));
    }
  };
};

export const deleteGroupCardAction = (groupId: string, cardId: string, cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(deleteGroupCardStart());
    try {
      await removeCardFromGroup(groupId, cardId);

      dispatch(deleteGroupCardSuccess());
      immediateToast("success", {
        message: "Card removed from Site/Cost Centre Successfully.",
        timeout: 3000,
        position: "topCenter"
      });
      dispatch(refreshGroupStatsAction([groupId]));
      if (cb) cb();
    } catch (err) {
      dispatch(deleteGroupCardFail(err));
    }
  };
};

export const searchExpenseAction = (groupId: string) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(searchExpenseStart());
    try {
      const response = await searchExpenses({
        size: 100,
        from: 0,
        filter: {
          expense_filter: {
            object_id: groupId
          }
        }
      });

      dispatch(
        searchExpenseSuccess({
          list: response.data.search_expense.items,
          total: response.data.search_expense.total_size
        })
      );
    } catch (err) {
      dispatch(searchExpenseFail(err));
    }
  };
};

export const saveGroupPreferencesAction = (data: SaveGroupPreferencesRequest, cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(saveGroupPreferencesStart());
    try {
      await saveGroupPreferences(data);

      dispatch(saveGroupPreferencesSuccess());
      immediateToast("success", {
        message: "Site/Cost Centre preferences saved.",
        timeout: 3000,
        position: "topCenter"
      });
      if (cb) cb();
    } catch (err) {
      dispatch(saveGroupPreferencesFail(err));
    }
  };
};

export const fetchConnectedUsersWalletsAction = (groupId: string) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchUserWalletsStart());
    try {
      const response = await getGroupWallets(groupId);
      const items = response.data?.get_group_wallets ?? [];

      const list = items.map((item: any) => ({
        wallet_id: item?.wallet_id,
        name: item?.name,
        description: item?.description,
        display_id: item?.display_id,
        balance: item?.balance ?? 0,
        wallet_type: item?.wallet_type,
        owner_id: item?.user?.user_id,
        owner_name: [item?.user?.attributes?.first_name, item?.user?.attributes?.last_name]
          .filter(Boolean)
          .join(" ")
          .trim()
      }));

      dispatch(fetchUserWalletsSuccess(list));
    } catch (err) {
      dispatch(fetchUserWalletsFail(err));
    }
  };
};

export default groupDetailSlice.reducer;
